/**
 * Shared NAME cell for native compact device tables (#999).
 *
 * One reusable renderer for all six device tables (racks, small/large UPS, power
 * distribution, cooling, liquid cooling) so the compact NAME column stays in
 * lockstep without per-type edits. Stacks, in order:
 *   1. Primary name text.
 *   2. Folded location tag (MapPin + tag) — step-view double-line, replaces the
 *      standalone ROOM column so NAME/PLATFORM/KW keep their width. The Devices
 *      view hides the location (it is the card title), so callers pass a null
 *      `locationTagName` there and only the status pill shows.
 *   3. Folded EXISTING / NEW status pill (Devices view only).
 *
 * Used by the shared compact layout on native mobile AND narrow/mobile web
 * (#999). Only wide desktop web keeps its standalone ROOM / STATUS columns.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';

import { recordTableStyles } from './recordTableStyles';
import { RepeatableUnitStatusPill } from './RepeatableUnitStatusPill';
import { LocationTagListSubline } from '../locationTags/LocationTagListSubline';

export interface NativeCompactNameCellProps {
  displayValue: string;
  isEmptyPlaceholder?: boolean;
  /** Location tag name to fold under NAME; omit/null hides it (Devices view). */
  locationTagName?: string | null;
  /** Show the folded EXISTING / NEW pill (Devices view `statusInColumn`). */
  showStatus?: boolean;
  status?: 'existing' | 'new' | string | null;
  testID?: string;
}

export const NativeCompactNameCell: React.FC<NativeCompactNameCellProps> = ({
  displayValue,
  isEmptyPlaceholder = false,
  locationTagName,
  showStatus = false,
  status,
  testID,
}) => {
  const tagName = locationTagName?.trim();

  return (
    // Fill the NAME column width. The cell wrapper uses `alignItems: flex-start`,
    // which would otherwise shrink this View to the (often short) primary-name
    // width and clip the location subline early. Stretching decouples the folded
    // location line's width from the name text so it can use the whole column (#999).
    <View style={styles.container}>
      <Text
        style={[
          recordTableStyles.modelPrimaryText,
          recordTableStyles.compactPrimaryText,
          isEmptyPlaceholder && recordTableStyles.placeholderText,
        ]}
        numberOfLines={1}
      >
        {displayValue}
      </Text>
      {tagName ? (
        <LocationTagListSubline
          locationTagName={tagName}
          style={[
            recordTableStyles.modelSublineText,
            recordTableStyles.compactSublineText,
          ]}
          compact
          testID={testID ? `${testID}-location` : undefined}
        />
      ) : null}
      {showStatus ? (
        <RepeatableUnitStatusPill
          status={status}
          testID={testID ? `${testID}-status` : undefined}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'stretch',
    width: '100%',
    minWidth: 0,
  },
});
